import { useState, useEffect, useRef } from "react";
import { useAsync } from "hooks";
import { isListed } from "data/utils";
import { fetchItemFlows } from "data/api";
import {
    ItemFlowId,
    ItemFlowConfigDTO,
    ItemFlowCategoryConfig,
    ItemFlowsConfigDTO,
    ItemFlowDirectionEnum,
} from "data/types";

type Props = {
    /** current folder's itemUri  */
    itemUri: string;
    /** optional target itemUri  */
    targetItemUri?: string;
    /** flow direction "in" vs "out" */
    direction?: ItemFlowDirectionEnum;
    /** optional flow.id to pre-select */
    flowId?: string;
    /** optional list of flow.ids to show */
    visibleFlowIds?: string[];
    /** optional category.id to pre-select */
    categoryId?: string;
    /** possibility to opt out, defaults to true */
    canFetch?: boolean;
};

const DefaultDocFlowIds: string[] = [
    ItemFlowId.doc_upload,
    ItemFlowId.doc_inNewFolder,
];

const isSelectedFlow = (
    flowId: string | undefined,
    flow: ItemFlowConfigDTO
): boolean => {
    if (flowId === ItemFlowId.doc_upload && flow.id) {
        return DefaultDocFlowIds.includes(flow.id);
    }
    return flow.id === flowId;
};

const useItemFlows = ({
    itemUri,
    targetItemUri,
    direction,
    flowId,
    visibleFlowIds,
    categoryId,
    canFetch = true,
}: Props): [
    ItemFlowConfigDTO | null,
    ItemFlowCategoryConfig | null,
    ReturnType<typeof useAsync>,
    React.Dispatch<React.SetStateAction<ItemFlowConfigDTO | null>>,
    React.Dispatch<React.SetStateAction<ItemFlowCategoryConfig | null>>
] => {
    const [selectedFlow, setSelectedFlow] = useState<ItemFlowConfigDTO | null>(
        null
    );
    const [selectedCategory, setSelectedCategory] =
        useState<ItemFlowCategoryConfig | null>(null);
    // We use a ref, since might set the selectedFlow in the useAsync...
    const allowFlowIdFromProps = useRef(true);
    // get input item flows
    const state = useAsync(async () => {
        if (!canFetch) return null;
        if (targetItemUri == null && itemUri == null) return null;
        try {
            const cfg: ItemFlowsConfigDTO = await fetchItemFlows(
                targetItemUri || itemUri,
                direction
            );
            // filter requested visible flows
            // PS: yes I know we could filter them on the server-side, but it would not improve performance just reduce packet size
            if (visibleFlowIds) {
                cfg.flows = cfg.flows.filter((f) =>
                    isListed(f.id, visibleFlowIds)
                );
            }
            // pre-select a specific flow
            if (cfg.selectFlowId) {
                allowFlowIdFromProps.current = false;
                setSelectedFlow(
                    cfg.flows.find((f) =>
                        isSelectedFlow(cfg.selectFlowId, f)
                    ) || null
                );
            } else if (cfg.flows.length === 1) {
                allowFlowIdFromProps.current = false;
                // pre-select if only one flow
                setSelectedFlow(cfg.flows[0]);
            } else {
                allowFlowIdFromProps.current = true;
            }
            return cfg;
        } catch (err) {
            console.warn("Error while fetchItemFlows", err);
            throw err;
        }
    }, [targetItemUri, itemUri, direction, visibleFlowIds, canFetch]);

    useEffect(() => {
        if (
            state.loading ||
            state.error ||
            !state.value ||
            !flowId ||
            !allowFlowIdFromProps.current
        )
            return;
        setSelectedFlow(
            state.value.flows.find((f) => isSelectedFlow(flowId, f)) || null
        );
    }, [state.loading, state.error, state.value, flowId]);

    useEffect(() => {
        if (state.loading || state.error || !state.value || !categoryId) return;
        setSelectedCategory(
            state.value.categories.find((f) => f.id === categoryId) || null
        );
    }, [state.loading, state.error, state.value, categoryId]);

    return [
        selectedFlow,
        selectedCategory,
        state,
        setSelectedFlow,
        setSelectedCategory,
    ];
};

export { useItemFlows, isSelectedFlow };
